import React, { ChangeEvent, Component, KeyboardEvent } from 'react';
import { SxProps, Theme } from '@mui/material';
import TextField from '@mui/material/TextField';

import { humanCase } from '@paragon/utilities';

import { PropertyConfiguration } from '../PropertySchema';
import { PropertyComponentProps } from './ModelProperty';

interface State {
  currentValue: string;
  isEditing: boolean;
}

interface GenericPropertyComponentConfiguration<TValue> {
  readonly sx?: SxProps<Theme>;

  inputProps?: Partial<{
    type: string;
    step: string;
    max: number;
    min: number;
  }>;

  parseValue(value: string): TValue;
  formatValue(value: TValue): string;
}

type Props<TValue> = PropertyComponentProps<TValue> & GenericPropertyComponentConfiguration<TValue>;

class GenericPropertyComponent<TValue> extends Component<Props<TValue>, State> {
  constructor(props: Props<TValue>) {
    super(props);

    this.state = {
      isEditing: false,
      currentValue: props.formatValue(props.value),
    };
  }

  get inputProps() {
    const result = { ...this.props.inputProps } as Record<string, any>;

    const possibleOptions = ['max', 'min'];
    for (const property of possibleOptions) {
      if (property in this.props.schema.configuration) {
        result[property] = this.props.schema.configuration[property as keyof PropertyConfiguration];
      }
    }

    return result;
  }

  onChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ currentValue: event.currentTarget.value });
  };

  onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
    this.setState({ isEditing: true });
  };

  onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onSubmit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.setState({ isEditing: false });
    }
  };

  onSubmit = () => {
    const currentValue = this.props.parseValue(this.state.currentValue);
    const noChange = this.props.value === currentValue;
    if (noChange) return;

    if (!this.isValid()) {
      this.setState({ currentValue: this.props.formatValue(this.props.value) });
      return;
    }

    this.props.onEdit(currentValue);
    this.setState({ isEditing: false });
  };

  isValid = () => (this.props.schema.configuration.required ? this.state.currentValue !== '' : true);

  render() {
    const eventHandlers = this.state.isEditing
      ? {
          onChange: this.onChange,
          onBlur: this.onSubmit,
          onKeyDown: this.onKeyDown,
        }
      : {
          onFocus: this.onFocus,
        };

    return (
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        required={this.props.schema.configuration.required}
        error={!this.isValid()}
        label={humanCase(this.props.name)}
        // The empty string fallback is necessary since this a controlled component
        value={this.state.currentValue ?? ''}
        disabled={this.props.schema.configuration.readOnly || this.props.configuration.disabled}
        slotProps={{ htmlInput: this.inputProps }}
        {...eventHandlers}
      />
    );
  }
}

const noChange = (value: any) => value;

// TODO: this only exists because generic components are hard...
export function buildGenericPropertyComponent<TValue extends string | number>(
  configuration: GenericPropertyComponentConfiguration<TValue> = {} as GenericPropertyComponentConfiguration<TValue>,
) {
  return (props: PropertyComponentProps<TValue>) => {
    if (!configuration.parseValue) configuration.parseValue = noChange;
    if (!configuration.formatValue) configuration.formatValue = noChange;

    return (
      <GenericPropertyComponent
        {...props}
        {...configuration}
        // tslint:disable-next-line max-line-length
        // The key is used to re-create the component when the value changes.  See https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
        key={props.value}
      />
    );
  };
}
